import {
  useContext,
  createContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

import { useWindowSize, constants } from "@newsela/angelou";

interface IActivityPanelContext {
  isActivityPanelExpanded: boolean;
  setIsActivityPanelExpanded: (arg: boolean) => void;
}

const ActivityPanelContext = createContext<IActivityPanelContext>({
  isActivityPanelExpanded: false,
  setIsActivityPanelExpanded: () => undefined,
});

export const ActivityPanelContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [isActivityPanelExpanded, setIsActivityPanelExpanded] =
    useState<boolean>(false);

  const { width: windowWidth } = useWindowSize();
  const {
    breakpoints: {
      Breakpoints: { sm, md },
    },
  } = constants;
  const isSmallScreen = windowWidth <= sm;
  const isMediumScreen = windowWidth <= md;
  useEffect(() => {
    // When the page is loaded on larger screens, the panel is expanded by default. On smaller screens it is collapsed by default.
    return isSmallScreen || isMediumScreen
      ? setIsActivityPanelExpanded(false)
      : setIsActivityPanelExpanded(true);
  }, [isSmallScreen, isMediumScreen]);

  return (
    <ActivityPanelContext.Provider
      value={{
        isActivityPanelExpanded,
        setIsActivityPanelExpanded,
      }}
    >
      {children}
    </ActivityPanelContext.Provider>
  );
};

const useActivityPanel = () => {
  const activityPanelContext = useContext(ActivityPanelContext);

  if (activityPanelContext === undefined) {
    throw new Error(
      "activityPanelContext must be used within a ActivityPanelContextProvider.",
    );
  }
  return { ...activityPanelContext };
};

export default useActivityPanel;
